import {connect} from 'react-redux';

import {ChannelModel} from '../../../../libs/shared/core/src/models/api/channelModel';
import {DraftMessageModel} from '../../../../libs/shared/core/src/models/api/messageModel';
import {
  getHasAiComposeV2FloatingButton,
  getHasComposeFloatingFormattingToolbar,
  getHasMarkdownInEmailComposer,
  getHasRepeatFormatting,
  getHasRtlLanguageSupport,
  getHasTablePasteIgnorePasteAsText,
  getHasTopicsInMessageTemplates,
} from '../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {
  AppParametricSelector,
  AppSelector,
  createSelector,
  makeMapStateToProps,
} from '../../../../srcCommon/helpers/reselectHelpers';
import {ComposerVariablesState} from '../../../helpers/models/applicationObjectVariableHelpers';
import {ConversationModel} from '../../../models/api/conversationModel';
import {LocalPreferencesFlagsEnum} from '../../../models/api/localPreferencesModel';
import {AppState} from '../../../store/appState';
import {fetchAccountsForCardsAsyncAction} from '../../../store/data/accounts/accountsActions';
import {getAdditionalVariablesFromApplicationObjects} from '../../../store/data/applications/applicationsObjectsSelectors';
import {
  getCompanyHasCsatSurveysFeature,
  getHasAiComposeFeature,
  getHasEnforcedCompanySignatures,
  getIsAiComposeEnabled,
} from '../../../store/data/company/companySelectors';
import {
  makeGetRecipientCardForInterpolation,
  makeGetRecipientForInterpolation,
  makeGetVisualRecipientMap,
} from '../../../store/data/contacts/recipientsSelectors';
import {toggleFlagAction} from '../../../store/data/localPreferences/localPreferencesActions';
import {getFlags} from '../../../store/data/localPreferences/localPreferencesSelectors';
import {
  makeGetDraftMessageNamespace,
  makeGetVariableInterpolator,
} from '../../../store/data/messages/messagesSelectors';
import {
  toggleListAutoFormattingAction,
  updateDefaultFontStyleAction,
  updateLastEmailFormatAction,
} from '../../../store/data/preferences/preferencesActions';
import {
  getDefaultFontStyle,
  getListAutoFormatting,
} from '../../../store/data/preferences/preferencesSelectors';
import {makeGetSelectedSignature} from '../../../store/data/signatures/signaturesSelectors';
import {getIsUserVisitor} from '../../../store/data/teammates/teammatesSelectors';
import {viewerConversationCloseRequestedAction} from '../../../store/panels/viewer/viewerPanelActions';
import {getEmojiValidationMap} from '../../../store/stateless/emojiSelectors';
import {getI18n} from '../../../store/stateless/internationalizationSelectors';
import {navigateToLinkAction} from '../../../store/stateless/navigationActions';
import {sanitizeDefaultFontStyle} from '../../common/editor/style/defaultFontStyle';
import {EmailComposer} from './emailComposer';

/*
 * Props.
 */

interface EmailComposerContainerProps {
  conversation: ConversationModel | null;
  message: DraftMessageModel;
  channel: ChannelModel;
  conflictLink?: string;
  variablesState?: ComposerVariablesState;
}

/*
 * Selectors.
 */

function getMessage(_state: AppState, props: EmailComposerContainerProps) {
  return props.message;
}

function getChannel(_state: AppState, props: EmailComposerContainerProps) {
  return props.channel;
}

function getSelectedTopicId(_state: AppState, props: EmailComposerContainerProps) {
  return props.variablesState?.topicId;
}

const getIsAiDisabled = createSelector(
  [getHasAiComposeFeature, getIsAiComposeEnabled],
  (hasAIComposeFeature, isAiComposeEnabled) => !hasAIComposeFeature || !isAiComposeEnabled,
);

const getSanitizedDefaultFontStyle = createSelector([getDefaultFontStyle], sanitizeDefaultFontStyle);

function makeGetRecipientCardForInterpolationFromDraft<P>(
  getDraft: AppParametricSelector<P, DraftMessageModel>,
) {
  const getDraftNamespace = makeGetDraftMessageNamespace(getDraft);
  const getVisualRecipientMap = makeGetVisualRecipientMap(getDraftNamespace, getDraft);
  const getRecipientForInterpolation = makeGetRecipientForInterpolation(getVisualRecipientMap);
  return makeGetRecipientCardForInterpolation(getRecipientForInterpolation);
}

const getIsFormattingToolbarOpen: AppSelector<boolean> = (state) => {
  const flags = getFlags(state);
  return flags[LocalPreferencesFlagsEnum.FORMATTING_TOOLBAR];
};

/*
 * Component.
 */

const mapStateToProps = makeMapStateToProps<EmailComposerContainerProps>()(() => ({
  interpolator: makeGetVariableInterpolator(getMessage, getSelectedTopicId),
  selectedSignature: makeGetSelectedSignature(getChannel, getMessage),
  defaultFontStyle: getSanitizedDefaultFontStyle,
  hasCsatSurveysFeature: getCompanyHasCsatSurveysFeature,
  i18n: getI18n,
  emojiValidationMap: getEmojiValidationMap,
  hasListAutoFormatting: getListAutoFormatting,
  hasRepeatFormatting: getHasRepeatFormatting,
  cardForInterpolation: makeGetRecipientCardForInterpolationFromDraft(getMessage),
  hasRtlLanguageSupport: getHasRtlLanguageSupport,
  hasAICompose: getHasAiComposeFeature,
  hasAiComposeV2FloatingButton: getHasAiComposeV2FloatingButton,
  isAiDisabled: getIsAiDisabled,
  hasTablePasteIgnorePasteAsText: getHasTablePasteIgnorePasteAsText,
  hasMarkdownInEmailComposer: getHasMarkdownInEmailComposer,
  hasTopicsInMessageTemplate: getHasTopicsInMessageTemplates,
  additionalVariables: getAdditionalVariablesFromApplicationObjects,
  isSignatureReadonly: getHasEnforcedCompanySignatures,
  isFormattingToolbarOpen: getIsFormattingToolbarOpen,
  isUserVisitor: getIsUserVisitor,
  isAiComposeEnabled: getIsAiComposeEnabled,
  hasFloatingFormattingToolbar: getHasComposeFloatingFormattingToolbar,
}));

export const EmailComposerContainer = connect(mapStateToProps, {
  updateLastEmailFormat: updateLastEmailFormatAction,
  updateDefaultFontStyle: updateDefaultFontStyleAction,
  navigateToLinkAction,
  fetchAccountsForCardsAsyncAction,
  toggleListAutoFormatting: toggleListAutoFormattingAction,
  viewerConversationCloseRequested: viewerConversationCloseRequestedAction,
  onIsFormattingToolbarOpenChange: (isOpen: boolean) =>
    toggleFlagAction(LocalPreferencesFlagsEnum.FORMATTING_TOOLBAR, isOpen),
})(EmailComposer);
